import React, { useState, useEffect } from 'react';
import { eventsAPI, adminAPI, feedbackAPI } from '../utils/api';
import AdminLogin from '../components/AdminLogin';
import toast from 'react-hot-toast';
import { QRCodeCanvas } from 'qrcode.react';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [consultationFilter, setConsultationFilter] = useState('all');
  const [registrationFilter, setRegistrationFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEvent, setNewEvent] = useState({
    name: '',
    date: '',
    time: '',
    venue: '',
    description: '',
    maxCapacity: 1000,
    ticketTiers: [],
    customFields: []
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [qrCheckInId, setQrCheckInId] = useState('');
  const [newProfessionOption, setNewProfessionOption] = useState('');
  const [newCustomField, setNewCustomField] = useState({ fieldName: '', fieldLabel: '', fieldType: 'text', required: false, options: [] });
  const [newFieldOption, setNewFieldOption] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [photoLink, setPhotoLink] = useState('');
  const [customFeedbackMessage, setCustomFeedbackMessage] = useState('');
  const [feedbackModalEvent, setFeedbackModalEvent] = useState(null);
  const [checkedInAttendees, setCheckedInAttendees] = useState([]);
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [messageType, setMessageType] = useState('');
  const [expandedLinkEvent, setExpandedLinkEvent] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsAuthenticated(true);
      fetchEvents();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchDashboardData(selectedEvent.eventId);
    }
    if (activeTab === 'consultations') {
      fetchConsultations();
    }
    if (activeTab === 'registrations' && selectedEvent) {
      fetchRegistrations();
    }
    if (activeTab === 'feedback' && selectedEvent) {
      fetchFeedbacks();
    }
  }, [selectedEvent, activeTab]);

  const handleLogin = (adminData) => {
    setAdmin(adminData);
    setIsAuthenticated(true);
    fetchEvents();
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setAdmin(null);
    setEvents([]);
    setSelectedEvent(null);
    setDashboardData(null);
  };

  const fetchEvents = async () => {
    try {
      console.log('Fetching events from:', process.env.REACT_APP_API_URL);
      const response = await eventsAPI.getAll();
      console.log('Events response:', response);
      
      const eventsData = Array.isArray(response.data) ? response.data : [];
      setEvents(eventsData);
      
      if (eventsData.length > 0 && !selectedEvent) {
        setSelectedEvent(eventsData[0]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async (eventId) => {
    try {
      const response = await adminAPI.getDashboard(eventId);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchConsultations = async () => {
    try {
      const rawApi = process.env.REACT_APP_API_URL;
      const API_URL = rawApi.endsWith('/api') ? rawApi : `${rawApi.replace(/\/$/, '')}/api`;
      const filter = consultationFilter === 'all' ? '' : `?status=${consultationFilter}`;
      const response = await fetch(`${API_URL}/consultations${filter}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Accept': 'application/json',
        }
      });
      const data = await response.json();
      setConsultations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching consultations:', error);
    }
  };

  const fetchRegistrations = async () => {
    try {
      const rawApi = process.env.REACT_APP_API_URL;
      const API_URL = rawApi.endsWith('/api') ? rawApi : `${rawApi.replace(/\/$/, '')}/api`;
      const response = await fetch(`${API_URL}/registrations/event/${selectedEvent.eventId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Accept': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch registrations');
      }
      
      const data = await response.json();
      setRegistrations(data.success && data.data ? data.data : Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      if (error.message.includes('Failed to fetch')) {
        toast.error('Cannot reach server. Check your internet connection or the API server may be down.');
      }
      setRegistrations([]);
    }
  };

  const fetchFeedbacks = async () => {
    try {
      const response = await feedbackAPI.getByEvent(selectedEvent.eventId);
      setFeedbacks(response.data.data || []);
      const statsResponse = await feedbackAPI.getStats(selectedEvent.eventId);
      setFeedbackStats(statsResponse.data.data || null);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      setFeedbacks([]);
    }
  };

  const updateConsultationStatus = async (id, status) => {
    try {
      const rawApi = process.env.REACT_APP_API_URL;
      const API_URL = rawApi.endsWith('/api') ? rawApi : `${rawApi.replace(/\/$/, '')}/api`;

      const response = await fetch(`${API_URL}/consultations/${id}/status`, {
        method: 'PATCH',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Server error' }));
        throw new Error(error.message || 'Failed to update status');
      }

      toast.success('Status updated successfully');
      fetchConsultations();
    } catch (error) {
      console.error('Update consultation error:', error);
      toast.error(error.message || 'Failed to update status');
    }
  };

  const handleCheckIn = async (registrationId) => {
    try {
      if (!registrationId || typeof registrationId !== 'string') {
        toast.error('Invalid registration ID');
        return;
      }

      const cleanId = registrationId.trim().toUpperCase();

      if (!/^[A-Z0-9]{8}$/.test(cleanId)) {
        toast.error('Invalid ID format. Must be 8 characters (letters/numbers)');
        return;
      }

      const rawApi = process.env.REACT_APP_API_URL;
      const API_URL = rawApi.endsWith('/api')
        ? rawApi
        : `${rawApi.replace(/\/$/, '')}/api`;

      // Use PATCH endpoint for check-in
      const url = `${API_URL}/registrations/${cleanId}/status`;
      console.log('[CHECK-IN] Calling URL:', url);

      const token = localStorage.getItem('adminToken');
      if (!token) {
        toast.error('Session expired. Please login again.');
        return;
      }

      console.log('[CHECK-IN] Sending PATCH request...');
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: 'checkedIn' })
      });

      console.log('[CHECK-IN] Response received:', response.status);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { message: `Server error: ${response.status}` };
        }

        if (response.status === 401) {
          toast.error('Session expired. Please login again.');
          handleLogout();
        } else if (response.status === 404) {
          toast.error(`Registration ID "${cleanId}" not found`);
        } else if (response.status === 400) {
          toast.error(errorData.message || 'Invalid request');
        } else {
          toast.error(errorData.message || 'Failed to check in user');
        }
        return;
      }

      const result = await response.json();
      
      // Handle already checked in case
      if (result.alreadyCheckedIn) {
        toast.info('User is already checked in!');
      } else {
        toast.success(result.message || 'User checked in successfully!');
      }
      
      fetchDashboardData(selectedEvent.eventId);
      if (activeTab === 'registrations') {
        fetchRegistrations();
      }

    } catch (error) {
      console.error('[CHECK-IN] Error:', error);

      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        toast.error('Cannot reach server. Check your internet connection or the API server may be down.');
      } else if (error.message.includes('CORS')) {
        toast.error('CORS error - contact your server administrator');
      } else {
        toast.error(error.message || 'Unexpected error during check-in');
      }
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    if (!newEvent.name || !newEvent.date || !newEvent.time || !newEvent.venue || !newEvent.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      let response;
      if (selectedImage) {
        const formData = new FormData();
        Object.keys(newEvent).forEach(key => {
          if (key === 'ticketTiers' || key === 'customFields') {
            formData.append(key, JSON.stringify(newEvent[key]));
          } else {
            formData.append(key, newEvent[key]);
          }
        });
        formData.append('image', selectedImage);
        response = await eventsAPI.createWithImage(formData);
      } else {
        response = await eventsAPI.create(newEvent);
      }
      
      toast.success('Event created successfully!');
      setShowCreateForm(false);
      setNewEvent({
        name: '',
        date: '',
        time: '',
        venue: '',
        description: '',
        maxCapacity: 1000,
        ticketTiers: [],
        customFields: []
      });
      setSelectedImage(null);
      setNewCustomField({ fieldName: '', fieldLabel: '', fieldType: 'text', required: false, options: [] });
      setNewFieldOption('');
      fetchEvents();
    } catch (error) {
      console.error('Event creation error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error creating event';
      toast.error(errorMessage);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      try {
        await eventsAPI.delete(eventId);
        toast.success('Event deleted successfully!');
        if (selectedEvent?.eventId === eventId) {
          setSelectedEvent(null);
        }
        fetchEvents();
      } catch (error) {
        console.error('Event deletion error:', error);
        toast.error('Error deleting event');
      }
    }
  };

  const exportData = async (type) => {
    try {
      const response = type === 'registrations' 
        ? await adminAPI.exportRegistrations(selectedEvent.eventId)
        : await adminAPI.exportCheckins(selectedEvent.eventId);
      
      const csvContent = "data:text/csv;charset=utf-8," 
        + Object.keys(response.data.data[0] || {}).join(",") + "\n"
        + response.data.data.map(row => Object.values(row).join(",")).join("\n");
      
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `${response.data.eventName}-${type}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`${type} data exported successfully!`);
    } catch (error) {
      toast.error(`Error exporting ${type} data`);
    }
  };

  const loadCheckedInAttendees = async (eventId) => {
    try {
      const rawApi = process.env.REACT_APP_API_URL;
      const API_URL = rawApi.endsWith('/api') ? rawApi : `${rawApi.replace(/\/$/, '')}/api`;
      const response = await fetch(`${API_URL}/registrations/event/${eventId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Accept': 'application/json',
        }
      });
      const data = await response.json();
      const regs = data.success && data.data ? data.data : Array.isArray(data) ? data : [];
      const checkedIn = regs.filter(r => r.isCheckedIn);
      setCheckedInAttendees(checkedIn);
      setSelectedAttendees(checkedIn.map(r => r.email));
    } catch (error) {
      console.error('Error loading attendees:', error);
      toast.error('Error loading attendees');
    }
  };

  const handleOpenFeedbackModal = () => {
    setShowFeedbackModal(true);
    setMessageType('');
    setFeedbackModalEvent(selectedEvent);
    if (selectedEvent) {
      loadCheckedInAttendees(selectedEvent.eventId);
    }
  };

  const handleEventChange = (eventId) => {
    const event = events.find(e => e.eventId === eventId);
    setFeedbackModalEvent(event);
    if (event) {
      loadCheckedInAttendees(event.eventId);
    }
  };

  const toggleAttendee = (email) => {
    setSelectedAttendees(prev => 
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const handleSendMessages = async () => {
    if (!messageType) {
      toast.error('Please select a message type');
      return;
    }
    if (selectedAttendees.length === 0) {
      toast.error('Please select at least one attendee');
      return;
    }
    try {
      let response;
      if (messageType === 'thankyou') {
        response = await feedbackAPI.sendThankYou(feedbackModalEvent.eventId, selectedAttendees, photoLink);
        toast.success(`Thank you emails sent to ${response.data.results.sent} attendees`);
      } else {
        response = await feedbackAPI.sendFeedbackRequest(feedbackModalEvent.eventId, selectedAttendees, customFeedbackMessage);
        toast.success(`Feedback requests sent to ${response.data.results.sent} attendees`);
      }
      setShowFeedbackModal(false);
      setPhotoLink('');
      setCustomFeedbackMessage('');
      setMessageType('');
    } catch (error) {
      console.error('Error sending messages:', error);
      toast.error('Error sending messages');
    }
  };

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-6 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <div className="flex gap-3">
            <button onClick={() => setShowCreateForm(!showCreateForm)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
              {showCreateForm ? 'Cancel' : 'Create New Event'}
            </button>
            <button onClick={handleLogout} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">
              Logout
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        ) : (
          <>
            {/* Create Event Form */}
            {showCreateForm && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">Create New Event</h2>
                <form onSubmit={handleCreateEvent} className="p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <input type="text" placeholder="Event Name" value={newEvent.name} onChange={(e) => setNewEvent({...newEvent, name: e.target.value})} className="input-field" required />
                    <input type="date" value={newEvent.date} onChange={(e) => setNewEvent({...newEvent, date: e.target.value})} className="input-field" required />
                    <input type="time" value={newEvent.time} onChange={(e) => setNewEvent({...newEvent, time: e.target.value})} className="input-field" required />
                    <input type="text" placeholder="Venue" value={newEvent.venue} onChange={(e) => setNewEvent({...newEvent, venue: e.target.value})} className="input-field" required />
                    <textarea placeholder="Description" value={newEvent.description} onChange={(e) => setNewEvent({...newEvent, description: e.target.value})} className="input-field col-span-2" rows="3" required />
                    <input type="file" accept="image/*" onChange={(e) => setSelectedImage(e.target.files[0])} className="input-field col-span-2" />
                    
                    {/* Custom Fields Configuration */}
                    <div className="col-span-2 border-t pt-4 mt-2">
                      <h4 className="font-semibold mb-3">Custom Registration Fields</h4>
                      
                      {/* Display existing custom fields */}
                      {newEvent.customFields.length > 0 && (
                        <div className="mb-4 space-y-2">
                          {newEvent.customFields.map((field, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-100 rounded">
                              <div>
                                <span className="font-medium">{field.fieldLabel}</span>
                                <span className="text-sm text-gray-600 ml-2">({field.fieldType})</span>
                                {field.required && <span className="text-xs text-red-600 ml-2">*Required</span>}
                                {field.options?.length > 0 && (
                                  <div className="text-xs text-gray-500 mt-1">Options: {field.options.join(', ')}</div>
                                )}
                              </div>
                              <button type="button" onClick={() => setNewEvent({...newEvent, customFields: newEvent.customFields.filter((_, i) => i !== idx)})} className="text-red-600 hover:text-red-800 font-bold">Remove</button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add new custom field */}
                      <div className="p-4 bg-blue-50 rounded border border-blue-200">
                        <h5 className="font-medium mb-3">Add New Custom Field</h5>
                        <div className="grid grid-cols-2 gap-3">
                          <input type="text" placeholder="Field Name (e.g., address)" value={newCustomField.fieldName} onChange={(e) => setNewCustomField({...newCustomField, fieldName: e.target.value.toLowerCase().replace(/\s+/g, '_')})} className="px-3 py-2 border rounded text-sm" />
                          <input type="text" placeholder="Field Label (e.g., Address)" value={newCustomField.fieldLabel} onChange={(e) => setNewCustomField({...newCustomField, fieldLabel: e.target.value})} className="px-3 py-2 border rounded text-sm" />
                          <select value={newCustomField.fieldType} onChange={(e) => setNewCustomField({...newCustomField, fieldType: e.target.value})} className="px-3 py-2 border rounded text-sm">
                            <option value="text">Text Input</option>
                            <option value="textarea">Text Area</option>
                            <option value="dropdown">Dropdown</option>
                          </select>
                          <label className="flex items-center">
                            <input type="checkbox" checked={newCustomField.required} onChange={(e) => setNewCustomField({...newCustomField, required: e.target.checked})} className="mr-2" />
                            <span className="text-sm">Required Field</span>
                          </label>
                        </div>
                        
                        {/* Dropdown options */}
                        {newCustomField.fieldType === 'dropdown' && (
                          <div className="mt-3">
                            <label className="text-sm font-medium mb-2 block">Dropdown Options:</label>
                            {newCustomField.options.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-2">
                                {newCustomField.options.map((opt, idx) => (
                                  <span key={idx} className="bg-white px-2 py-1 rounded text-sm flex items-center gap-1">
                                    {opt}
                                    <button type="button" onClick={() => setNewCustomField({...newCustomField, options: newCustomField.options.filter((_, i) => i !== idx)})} className="text-red-600 hover:text-red-800">×</button>
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="flex gap-2">
                              <input type="text" placeholder="Add option" value={newFieldOption} onChange={(e) => setNewFieldOption(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (newFieldOption.trim()) { setNewCustomField({...newCustomField, options: [...newCustomField.options, newFieldOption.trim()]}); setNewFieldOption(''); } } }} className="flex-1 px-3 py-1 border rounded text-sm" />
                              <button type="button" onClick={() => { if (newFieldOption.trim()) { setNewCustomField({...newCustomField, options: [...newCustomField.options, newFieldOption.trim()]}); setNewFieldOption(''); } }} className="bg-white px-3 py-1 rounded text-sm border">Add</button>
                            </div>
                          </div>
                        )}
                        
                        <button type="button" onClick={() => { if (newCustomField.fieldName && newCustomField.fieldLabel) { if (newCustomField.fieldType === 'dropdown' && newCustomField.options.length === 0) { toast.error('Please add at least one option for dropdown'); return; } setNewEvent({...newEvent, customFields: [...newEvent.customFields, {...newCustomField}]}); setNewCustomField({ fieldName: '', fieldLabel: '', fieldType: 'text', required: false, options: [] }); setNewFieldOption(''); toast.success('Custom field added!'); } else { toast.error('Please fill field name and label'); } }} className="mt-3 bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">Add Custom Field</button>
                      </div>
                    </div>
                  </div>
                  <button type="submit" className="mt-4 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700">Create Event</button>
                </form>
              </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="flex border-b">
                {['dashboard', 'registrations', 'consultations', /* 'feedback', */ 'events'].map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-3 font-medium ${activeTab === tab ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}>
                    {tab === 'events' ? 'Event Details' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Event Details Tab */}
            {activeTab === 'events' && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">All Events</h3>
                <div className="space-y-4">
                  {events.map(event => (
                    <div key={event.eventId} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900">{event.name}</h4>
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <p><strong>Date:</strong> {new Date(event.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                            <p><strong>Time:</strong> {event.time}</p>
                            <p><strong>Venue:</strong> {event.venue}</p>
                            <p><strong>Description:</strong> {event.description}</p>
                            <p><strong>Max Capacity:</strong> {event.maxCapacity}</p>
                            {event.registrationCount !== undefined && (
                              <p><strong>Registrations:</strong> {event.registrationCount} / {event.maxCapacity}</p>
                            )}
                            {event.customFields && event.customFields.length > 0 && (
                              <div className="mt-2">
                                <strong>Custom Fields:</strong>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {event.customFields.map((field, idx) => (
                                    <span key={idx} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                      {field.fieldLabel} ({field.fieldType}){field.required && ' *'}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <button onClick={() => { setSelectedEvent(event); setActiveTab('dashboard'); }} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm whitespace-nowrap">
                            View Dashboard
                          </button>
                          <button onClick={() => handleDeleteEvent(event.eventId)} className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm whitespace-nowrap">
                            Delete Event
                          </button>
                          <button
                            onClick={() => setExpandedLinkEvent(expandedLinkEvent === event.eventId ? null : event.eventId)}
                            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm whitespace-nowrap"
                          >
                            {expandedLinkEvent === event.eventId ? 'Hide Link' : 'Generate Link'}
                          </button>
                        </div>
                      </div>

                      {expandedLinkEvent === event.eventId && (() => {
                        const eventUrl = `${window.location.origin}/events/${event.eventId}`;
                        return (
                          <div className="mt-4 pt-4 border-t">
                            <h5 className="font-semibold text-gray-700 mb-3">Generate Link</h5>
                            <div className="flex items-center gap-2 mb-4">
                              <input
                                readOnly
                                value={eventUrl}
                                className="flex-1 px-3 py-2 border rounded text-sm bg-gray-50 text-gray-700"
                              />
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(eventUrl);
                                  toast.success('Link copied!');
                                }}
                                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm whitespace-nowrap"
                              >
                                Copy Link
                              </button>
                            </div>
                            <div className="flex items-start gap-4">
                              <QRCodeCanvas
                                id={`qr-canvas-${event.eventId}`}
                                value={eventUrl}
                                size={140}
                                level="H"
                                includeMargin
                              />
                              <button
                                onClick={() => {
                                  const canvas = document.getElementById(`qr-canvas-${event.eventId}`);
                                  if (canvas) {
                                    const a = document.createElement('a');
                                    a.href = canvas.toDataURL('image/png');
                                    a.download = `${event.name}-qr.png`;
                                    a.click();
                                  } else {
                                    toast.error('QR code not ready');
                                  }
                                }}
                                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm"
                              >
                                Download QR
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedEvent && activeTab !== 'events' && (
              <>
                {/* Dashboard Tab */}
                {activeTab === 'dashboard' && dashboardData && (
                  <div className="space-y-6">
                    {/* QR Check-in Section */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-semibold mb-4">Quick Check-in by QR ID</h3>
                      <div className="flex gap-4">
                        <input
                          type="text"
                          placeholder="Enter Registration ID (e.g., ABC12345)"
                          value={qrCheckInId}
                          onChange={(e) => setQrCheckInId(e.target.value.toUpperCase())}
                          className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          maxLength={8}
                        />
                        <button
                          onClick={() => {
                            if (qrCheckInId) {
                              handleCheckIn(qrCheckInId);
                              setQrCheckInId('');
                            } else {
                              toast.error('Please enter a registration ID');
                            }
                          }}
                          className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 font-medium"
                        >
                          Check In
                        </button>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">Enter the 8-character registration ID from the QR code</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-white p-6 rounded-lg shadow">
                        <h3 className="text-gray-600 text-sm">Total Registrations</h3>
                        <p className="text-3xl font-bold text-blue-600">{dashboardData.data?.statistics?.totalRegistrations || 0}</p>
                      </div>
                      <div className="bg-white p-6 rounded-lg shadow">
                        <h3 className="text-gray-600 text-sm">Checked In</h3>
                        <p className="text-3xl font-bold text-green-600">{dashboardData.data?.statistics?.checkedIn || 0}</p>
                      </div>
                      <div className="bg-white p-6 rounded-lg shadow">
                        <h3 className="text-gray-600 text-sm">Pending</h3>
                        <p className="text-3xl font-bold text-yellow-600">{dashboardData.data?.statistics?.pending || 0}</p>
                      </div>
                      <div className="bg-white p-6 rounded-lg shadow">
                        <h3 className="text-gray-600 text-sm">Check-in Rate</h3>
                        <p className="text-3xl font-bold text-purple-600">{dashboardData.data?.statistics?.checkInRate || 0}%</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-semibold mb-4">Recent Registrations</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead><tr className="border-b"><th className="text-left py-2">Name</th><th className="text-left py-2">Email</th><th className="text-left py-2">Ticket</th><th className="text-left py-2">Status</th></tr></thead>
                          <tbody>{dashboardData.data?.recentRegistrations?.map(reg => (<tr key={reg._id} className="border-b"><td className="py-2">{reg.name}</td><td className="py-2">{reg.email}</td><td className="py-2">{reg.ticketTier}</td><td className="py-2"><span className={`px-2 py-1 rounded text-xs ${reg.isCheckedIn ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{reg.isCheckedIn ? 'Checked In' : 'Pending'}</span></td></tr>))}</tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Registrations Tab */}
                {activeTab === 'registrations' && (
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold">Registrations</h3>
                      <button onClick={() => exportData('registrations')} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Export CSV</button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead><tr className="border-b"><th className="text-left py-2">ID</th><th className="text-left py-2">Name</th><th className="text-left py-2">Email</th><th className="text-left py-2">Phone</th><th className="text-left py-2">Custom Fields</th><th className="text-left py-2">Status</th><th className="text-left py-2">Action</th></tr></thead>
                        <tbody>{registrations.map(reg => (<tr key={reg._id} className="border-b"><td className="py-2">{reg.registrationId}</td><td className="py-2">{reg.name}</td><td className="py-2">{reg.email}</td><td className="py-2">{reg.phone}</td><td className="py-2"><div className="text-xs">{reg.customFieldValues && Object.keys(reg.customFieldValues).length > 0 ? Object.entries(reg.customFieldValues).map(([key, value]) => value ? <div key={key}><strong>{key}:</strong> {value}</div> : null) : '-'}</div></td><td className="py-2"><span className={`px-2 py-1 rounded text-xs ${reg.isCheckedIn ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{reg.isCheckedIn ? 'Checked In' : 'Pending'}</span></td><td className="py-2">{!reg.isCheckedIn && <button onClick={() => handleCheckIn(reg.registrationId)} className="text-blue-600 hover:underline text-sm">Check In</button>}</td></tr>))}</tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Consultations Tab */}
                {activeTab === 'consultations' && (
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4">Consultation Requests</h3>
                    <div className="space-y-4">
                      {consultations.map(cons => (
                        <div key={cons._id} className="border rounded-lg p-4">
                          <div className="flex justify-between">
                            <div><h4 className="font-semibold">{cons.contact}</h4><p className="text-sm text-gray-600">{cons.email} | {cons.phone}</p><p className="text-sm mt-2">{cons.requirements}</p></div>
                            <select value={cons.status} onChange={(e) => updateConsultationStatus(cons.consultationId, e.target.value)} className="h-10 border rounded px-2"><option value="pending">Pending</option><option value="completed">Completed</option><option value="checkedIn">Checked In</option></select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Feedback Tab - commented out */}
              </>
            )}
          </>
        )}
      </div>

      {/* Feedback Modal - commented out */}
    </div>
  );
};

export default Admin;